import type { Artist, Contact, ContactRole } from "../types";
import { artistDisplayName } from "./artistProfile";

export const CONTACT_ROLE_LABELS: Record<ContactRole, string> = {
  agent: "Agent",
  manager: "Manager",
  label: "Label",
  co_promoter: "Co-promoter",
};

export type ContactFormState = {
  name: string;
  role: ContactRole | "";
  agency: string;
  email: string;
  phone: string;
  notes: string;
  artist_ids: string[];
};

export const emptyContactForm: ContactFormState = {
  name: "",
  role: "",
  agency: "",
  email: "",
  phone: "",
  notes: "",
  artist_ids: [],
};

export function contactToForm(contact: Contact): ContactFormState {
  return {
    name: contact.name,
    role: (contact.role as ContactRole) ?? "",
    agency: contact.agency ?? "",
    email: contact.email ?? "",
    phone: contact.phone ?? "",
    notes: contact.notes ?? "",
    artist_ids: contact.artists?.map((artist) => artist.id) ?? [],
  };
}

export function buildContactPayload(form: ContactFormState) {
  return {
    name: form.name.trim(),
    role: form.role || null,
    agency: form.agency.trim() || null,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    notes: form.notes.trim() || null,
    artist_ids: form.artist_ids,
  };
}

/** Strip non-digits and return E.164-style digits for India (91 prefix). */
export function normalizeIndianPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("91") && digits.length >= 12) return digits;
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

export function formatContactPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  if (digits.startsWith("91") && digits.length === 12) {
    const local = digits.slice(2);
    return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  }
  return phone.trim();
}

export function buildWhatsAppUrl(phone: string): string | null {
  const normalized = normalizeIndianPhone(phone);
  if (!normalized) return null;
  return `https://wa.me/${normalized}`;
}

export function formatContactRole(role?: string | null): string {
  if (!role) return "—";
  return CONTACT_ROLE_LABELS[role as ContactRole] ?? role;
}

export function formatContactSummary(contact: Contact): string {
  const email = contact.email?.trim();
  const phone = contact.phone?.trim();
  if (email || phone) {
    return [email, phone ? formatContactPhoneDisplay(phone) : null].filter(Boolean).join(" · ");
  }
  return "—";
}

/**
 * The contact's artist summaries only carry the legal name, so pass the full
 * artist list to resolve each one's stage name.
 */
export function formatLinkedArtists(contact: Contact, artists: Pick<Artist, "id" | "name" | "stage_name">[] = []): string {
  if (!contact.artists?.length) return "—";
  return contact.artists
    .map((linked) => {
      const full = artists.find((artist) => artist.id === linked.id);
      return full ? artistDisplayName(full) : linked.name;
    })
    .join(", ");
}
